import { AccrualMatchedTransactionDisplay } from "@common/schemas/domains/accrualReview/accrual-review.schema";
import { InvoiceLineItem } from "@common/types/invoice";
import { useInvoiceAccrualMatches } from "@api/invoice.api";
import { EditableCellContent } from "@design-system/ViewTable/EditableCellContent";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { CellContext } from "@tanstack/react-table";
import {
    TransactionPreview,
    TransactionPreviewData,
} from "@components/transactions/TransactionPreview/TransactionPreview";
import { useOverlays } from "contexts/OverlayContext";
import { memo, useCallback } from "react";
import styles from "./MatchCell.module.scss";

export function toMatchedTransactionPreview(
    match: AccrualMatchedTransactionDisplay,
): TransactionPreviewData {
    return {
        id: match.transactionId,
        date: match.date,
        bankDescription: match.bankDescription,
        displayDescription: match.displayDescription,
        entityId: match.entity.id,
        financialAccountId: match.financialAccountId ?? undefined,
        amount: match.amount,
    };
}

export const MatchCell = memo(
    ({ row }: CellContext<ViewTableFeatures, InvoiceLineItem, unknown>) => {
        const lineItem = row.original;
        const { openOverlay } = useOverlays();
        const matchesQuery = useInvoiceAccrualMatches(lineItem.invoiceId);
        const firstMatch = matchesQuery.data?.[0];
        const preview = firstMatch
            ? toMatchedTransactionPreview(firstMatch)
            : null;

        const handleOpenMatchDrawer = useCallback(() => {
            openOverlay("documentAccrualMatch", {
                documentType: "invoice",
                documentId: lineItem.invoiceId,
            });
        }, [lineItem.invoiceId, openOverlay]);

        if (matchesQuery.isPending) {
            return <ViewTableCell />;
        }

        return (
            <ViewTableCell captureClicks className={styles.cell}>
                <EditableCellContent
                    className={preview ? styles.match : undefined}
                    isEmpty={!preview}
                    onClick={handleOpenMatchDrawer}
                >
                    {preview && <TransactionPreview transaction={preview} />}
                </EditableCellContent>
            </ViewTableCell>
        );
    },
);
MatchCell.displayName = "MatchCell";
